import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import {
  useContacts,
  useContactGroups,
  useGenerateMatchScore,
} from "@/features/contacts/hooks/useContacts";
import {
  useAddLeadsToCampaign,
  useGetCampaignById,
} from "@/features/campaigns/hooks/useCampaign";
import MatchScoreReviewModal from "@/features/contacts/components/MatchScoreReviewModal";
import {
  applyEmailValidationResults,
  buildScoredContacts,
} from "@/features/contacts/utils/matchScoreReview";
import { preflightEnrollment } from "@/features/campaigns/api/enrollmentsApi";
import { useMainStore } from "@/stores";
import Loader from "@/shared/components/ui/Loaders/Loader";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import PaginationInfo from "@/shared/components/ui/Pagination/PaginationInfo";
import NoDataFound from "@/shared/components/ui/NoDataView/NoDataFound";
import CommonGroupBy from "@/shared/components/ui/DropDown/CommonGroupBy";
import ContactFiltersUI from "@/features/contacts/components/ContactFiltersUI";
import GroupedContactTableSection from "@/features/contacts/components/GroupedContactTableSection";
import { ListFilter, X } from "lucide-react";
import {
  ContactColumns,
  renderContactCell,
  CONTACTS_GROUP_BY_OPTIONS,
  CONTACTS_GROUP_BY_PLACEHOLDER_ICON,
  compareContactMatchFitGroupLabels,
  compareContactCreatedDateGroupLabels,
} from "@/shared/utils/contactConfig";
import { DEFAULT_FILTERS } from "@/shared/utils/utils";
import {
  fetchGroupContactsAPI,
  fetchAllContactIdsAPI,
} from "@/features/contacts/api/contactsApi";
import { showError } from "@/shared/utils/toast";

const ROWS_PER_PAGE = 30;

const buildMatchScoreCacheKey = (userId, contactIds) =>
  JSON.stringify({
    userId: String(userId),
    contactIds: contactIds.map(String).sort(),
  });

const ContactsListModal = ({ isOpen, onClose, campaignId }) => {
  const user = useMainStore((state) => state.user);
  const { data: campaignData } = useGetCampaignById(campaignId, isOpen);
  const campaign = campaignData?.data ?? campaignData;
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [reviewContacts, setReviewContacts] = useState([]);
  const [isValidatingEmails, setIsValidatingEmails] = useState(false);
  const matchScoreCacheRef = useRef(new Map());

  const [groupBy, setGroupBy] = useState(null);
  const isGrouped = Boolean(groupBy);

  const [showfilters, setShowfilters] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState({});

  const [expandedGroups, setExpandedGroups] = useState({});
  const [openGroups, setOpenGroups] = useState(new Set());
  const [sortDescriptor, setSortDescriptor] = useState({
    column: "",
    direction: null,
  });
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());

  const {
    contacts,
    totalCount: apiTotalCount,
    isLoading: isLoadingContacts,
  } = useContacts(
    page,
    ROWS_PER_PAGE,
    "",
    searchValue,
    appliedFilters,
    !isGrouped && isOpen,
  );

  const { data: groupsData, isLoading: isLoadingGroups } = useContactGroups(
    groupBy,
    page,
    ROWS_PER_PAGE,
    searchValue,
    appliedFilters,
    isGrouped && isOpen,
  );

  const {
    mutate: addLeadsToCampaign,
    isPending: isAdding,
    reset: resetAddMutation,
  } = useAddLeadsToCampaign();
  const {
    mutateAsync: generateMatchScoreAsync,
    isPending: isGeneratingMatchScore,
  } = useGenerateMatchScore();

  const displayGroups = useMemo(() => {
    if (!isGrouped) return [];
    const groups = groupsData?.groups ?? [];
    if (groupBy === "match_fit") {
      return [...groups].sort((a, b) =>
        compareContactMatchFitGroupLabels(a.name, b.name),
      );
    }
    if (groupBy === "created_date" || groupBy === "created_at") {
      return [...groups].sort((a, b) =>
        compareContactCreatedDateGroupLabels(a.name, b.name),
      );
    }
    return groups;
  }, [isGrouped, groupsData, groupBy]);

  const totalGroups = useMemo(() => {
    if (!isGrouped) return 0;
    return groupsData?.total ?? 0;
  }, [isGrouped, groupsData]);

  const totalCount = isGrouped ? totalGroups : (apiTotalCount ?? 0);
  const isLoading = isGrouped ? isLoadingGroups : isLoadingContacts;

  const appliedFilterCount = useMemo(
    () =>
      Object.values(appliedFilters).filter((v) =>
        Array.isArray(v) ? v.length > 0 : Boolean(v),
      ).length,
    [appliedFilters],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
      setPage(1);
      setSelectedContacts([]);
      setShowReview(false);
      setReviewContacts([]);
      matchScoreCacheRef.current.clear();
      setGroupBy(null);
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters({});
      setShowfilters(false);
      setExpandedGroups({});
      setOpenGroups(new Set());
      setSelectedGroupIds(new Set());
      resetAddMutation();
    }
  }, [isOpen, resetAddMutation]);

  useEffect(() => {
    setPage(1);
  }, [searchValue]);

  useEffect(() => {
    setExpandedGroups({});
    setOpenGroups(new Set());
  }, [searchValue, appliedFilters, groupBy, sortDescriptor]);

  const handleSelectionChange = useCallback((contactsList) => {
    setSelectedContacts(contactsList);
  }, []);

  const toggleGroupExpand = useCallback(
    async (groupId, autoSelect = false) => {
      const isCurrentlyOpen = openGroups.has(groupId);
      const nextOpenGroups = new Set(openGroups);

      if (isCurrentlyOpen && !autoSelect) {
        nextOpenGroups.delete(groupId);
        setOpenGroups(nextOpenGroups);
      } else {
        if (!isCurrentlyOpen) {
          nextOpenGroups.add(groupId);
          setOpenGroups(nextOpenGroups);
        }

        if (!expandedGroups[groupId]) {
          setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: {
              contacts: [],
              nextCursor: null,
              hasMore: false,
              loading: true,
            },
          }));

          try {
            const res = await fetchGroupContactsAPI(
              groupBy,
              groupId,
              20,
              null,
              searchValue,
              appliedFilters,
              sortDescriptor,
            );

            setExpandedGroups((prev) => ({
              ...prev,
              [groupId]: {
                contacts: res.contacts,
                nextCursor: res.nextCursor,
                hasMore: res.hasMore,
                loading: false,
              },
            }));

            if (autoSelect) {
              const currentSelected = selectedContacts || [];
              const existingIds = new Set(currentSelected.map((c) => c.id));
              const toAdd = res.contacts
                .filter((c) => !existingIds.has(c.id))
                .map((c) => ({
                  id: c.id,
                  name: c.name,
                  email: c.email,
                  company: c.company,
                }));
              setSelectedContacts([...currentSelected, ...toAdd]);
            }
          } catch (err) {
            showError(err.message || "Failed to load contacts for group.");
            setExpandedGroups((prev) => ({
              ...prev,
              [groupId]: {
                contacts: [],
                nextCursor: null,
                hasMore: false,
                loading: false,
              },
            }));
          }
        } else if (autoSelect) {
          const groupContacts = expandedGroups[groupId]?.contacts || [];
          const currentSelected = selectedContacts || [];
          const existingIds = new Set(currentSelected.map((c) => c.id));
          const toAdd = groupContacts
            .filter((c) => !existingIds.has(c.id))
            .map((c) => ({
              id: c.id,
              name: c.name,
              email: c.email,
              company: c.company,
            }));
          setSelectedContacts([...currentSelected, ...toAdd]);
        }
      }
    },
    [
      openGroups,
      expandedGroups,
      groupBy,
      searchValue,
      appliedFilters,
      sortDescriptor,
      selectedContacts,
    ],
  );

  const loadMoreGroupContacts = useCallback(
    async (groupId) => {
      const groupState = expandedGroups[groupId];
      if (
        !groupState ||
        groupState.loading ||
        !groupState.hasMore ||
        !groupState.nextCursor
      )
        return;

      setExpandedGroups((prev) => ({
        ...prev,
        [groupId]: { ...prev[groupId], loading: true },
      }));

      try {
        const res = await fetchGroupContactsAPI(
          groupBy,
          groupId,
          20,
          groupState.nextCursor,
          searchValue,
          appliedFilters,
          sortDescriptor,
        );

        setExpandedGroups((prev) => ({
          ...prev,
          [groupId]: {
            contacts: [...groupState.contacts, ...res.contacts],
            nextCursor: res.nextCursor,
            hasMore: res.hasMore,
            loading: false,
          },
        }));
      } catch (err) {
        showError(err.message || "Failed to load more contacts for group.");
        setExpandedGroups((prev) => ({
          ...prev,
          [groupId]: { ...prev[groupId], loading: false },
        }));
      }
    },
    [expandedGroups, groupBy, searchValue, appliedFilters, sortDescriptor],
  );

  const toggleGroupSelection = useCallback(
    async (groupId, shouldSelect) => {
      try {
        const res = await fetchAllContactIdsAPI(searchValue, {
          ...appliedFilters,
          groupBy,
          groupId,
        });

        const currentSelected = selectedContacts || [];
        const currentSelectedIds = new Set(currentSelected.map((c) => c.id));

        let nextSelected;
        if (shouldSelect) {
          const details = res.length
            ? await fetchGroupContactsAPI(
                groupBy,
                groupId,
                res.length,
                null,
                searchValue,
                appliedFilters,
              )
            : { contacts: [] };
          const contactsById = new Map(
            details.contacts.map((contact) => [String(contact.id), contact]),
          );
          const selectedWithDetails = currentSelected.map((contact) => {
            const detailsForContact = contactsById.get(String(contact.id));
            return detailsForContact
              ? { ...contact, ...detailsForContact }
              : contact;
          });
          const toAdd = res
            .filter((id) => !currentSelectedIds.has(id))
            .map((id) => {
              const contact = contactsById.get(String(id));
              return {
                id,
                name: contact?.name || "",
                email: contact?.email || "",
                company: contact?.company || "",
              };
            });
          nextSelected = [...selectedWithDetails, ...toAdd];
          setSelectedGroupIds((prev) => {
            const next = new Set(prev);
            next.add(groupId);
            return next;
          });
        } else {
          const groupIdsSet = new Set(res);
          nextSelected = currentSelected.filter((c) => !groupIdsSet.has(c.id));
          setSelectedGroupIds((prev) => {
            const next = new Set(prev);
            next.delete(groupId);
            return next;
          });
        }

        setSelectedContacts(nextSelected);
      } catch (err) {
        showError(err.message || "Failed to toggle group selection.");
      }
    },
    [groupBy, searchValue, appliedFilters, selectedContacts],
  );

  const applyFilters = useCallback(() => {
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) =>
        Array.isArray(v) ? v.length > 0 : Boolean(v),
      ),
    );
    setAppliedFilters(cleaned);
    setPage(1);
    setShowfilters(false);
  }, [filters]);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters({});
    setSearchValue("");
    setPage(1);
    setShowfilters(false);
  }, []);

  const handleAddSelected = useCallback(async () => {
    if (
      !campaignId ||
      selectedContacts.length === 0 ||
      isAdding ||
      isGeneratingMatchScore
    )
      return;

    const selectedWithIds = selectedContacts.filter((contact) => contact?.id);
    if (selectedWithIds.length === 0) return;
    if (!user?.id) {
      showError("Unable to generate match scores without a signed-in user.");
      return;
    }

    try {
      const contactIds = selectedWithIds.map((contact) => contact.id);
      const cacheKey = buildMatchScoreCacheKey(user.id, contactIds);
      let scoreRequest = matchScoreCacheRef.current.get(cacheKey);

      if (!scoreRequest) {
        scoreRequest = generateMatchScoreAsync({
          userId: user.id,
          contactIds,
        });
        matchScoreCacheRef.current.set(cacheKey, scoreRequest);
      }

      let scoreResponse;
      try {
        scoreResponse = await scoreRequest;
      } catch (error) {
        matchScoreCacheRef.current.delete(cacheKey);
        throw error;
      }

      setIsValidatingEmails(true);
      const validationResponse = await preflightEnrollment({
        campaignId,
        contactIds,
      });
      const scoredContacts = buildScoredContacts(
        selectedWithIds,
        scoreResponse,
        campaignId,
      );
      setReviewContacts(applyEmailValidationResults(scoredContacts, validationResponse));
      setShowReview(true);
    } catch (error) {
      showError(
        error?.message ||
          "Failed to generate match scores. No leads were added to the campaign.",
      );
    } finally {
      setIsValidatingEmails(false);
    }
  }, [
    campaignId,
    generateMatchScoreAsync,
    isAdding,
    isGeneratingMatchScore,
    selectedContacts,
    user?.id,
  ]);

  const handleConfirmEnrollment = useCallback(
    (contactsToAdd) => {
      if (!campaignId || contactsToAdd.length === 0 || isAdding) return;

      const formattedContacts = contactsToAdd
        .filter((c) => c?.id)
        .map((c) => ({ contactId: c.id }));

      if (formattedContacts.length === 0) return;

      addLeadsToCampaign({
        campaignId,
        contacts: formattedContacts,
        userId: user?.id,
        action: onClose,
      });
    },
    [addLeadsToCampaign, campaignId, isAdding, onClose, user?.id],
  );

  const isBusy = isAdding || isGeneratingMatchScore || isValidatingEmails;

  return (
    <>
      <CustomModal
        isOpen={isOpen && !showReview}
        onClose={onClose}
        width="1100px"
        className="p-6 max-h-[calc(100dvh-80px)]"
      >
        <div className="relative h-[calc(100dvh-130px)]">
          {isBusy && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-lg">
              <Loader
                text={
                  isGeneratingMatchScore
                    ? "Generating match scores for selected leads..."
                    : isValidatingEmails
                      ? "Validating email addresses..."
                    : "Adding leads to campaign and generating content..."
                }
              />
            </div>
          )}
          {!isBusy &&
            isLoading &&
            ((!isGrouped && contacts.length === 0) ||
              (isGrouped && displayGroups.length === 0)) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-lg">
                <Loader text="Loading contacts..." />
              </div>
            )}
          <div className="flex flex-col gap-2.5 h-full w-full">
            <div className="flex justify-between items-start w-full">
              <div className="flex flex-col">
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Add Leads</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Search and select contacts to add to this campaign
                </p>
              </div>
              <button
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                onClick={onClose}
                disabled={isBusy}
              >
                <X size={20} className="text-gray-800 dark:text-gray-200" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-4 w-full flex-wrap">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <SearchBar
                  value={searchValue}
                  setFilterValue={setSearchValue}
                  onChange={setSearchValue}
                  placeholder="Search contacts..."
                  className="w-full max-w-[280px]"
                />
                <PaginationInfo
                  totalCount={totalCount}
                  currentPage={page}
                  rowsPerPage={ROWS_PER_PAGE}
                  itemName={isGrouped ? "group" : "contact"}
                />
              </div>
              <div className="flex items-center gap-2">
                <CommonGroupBy
                  groupBy={groupBy}
                  onSetGroupBy={setGroupBy}
                  options={CONTACTS_GROUP_BY_OPTIONS}
                  placeholderIcon={CONTACTS_GROUP_BY_PLACEHOLDER_ICON}
                />
                <SecondaryButton
                  title={
                    <span>
                      Filters
                      {appliedFilterCount > 0 && (
                        <span className="text-white text-[0.65rem] px-1.5 py-0.5 bg-secondary rounded-full ml-1">
                          {appliedFilterCount}
                        </span>
                      )}
                    </span>
                  }
                  icon={
                    <ListFilter
                      size={16}
                      className={`${appliedFilterCount > 0 ? "text-secondary" : "text-gray-800 dark:text-gray-200"}`}
                    />
                  }
                  onClick={() => setShowfilters(true)}
                  className={`shadow-none ${appliedFilterCount > 0 ? "border-secondary text-secondary bg-secondary/5" : ""}`}
                />
              </div>
            </div>

            {isGrouped ? (
              displayGroups.length === 0 && !isLoading ? (
                <div className="flex flex-col items-center justify-center min-h-[200px] gap-2 flex-1">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    No Groups Found
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No contact groups matched your search or filters.
                  </p>
                </div>
              ) : (
                <div className="flex-1 min-h-0 w-full flex flex-col">
                  <GroupedContactTableSection
                    groups={displayGroups}
                    expandedGroups={expandedGroups}
                    openGroups={openGroups}
                    onToggleExpand={toggleGroupExpand}
                    onLoadMore={loadMoreGroupContacts}
                    columns={ContactColumns?.filter(
                      (col) => col.uid !== "actions",
                    )}
                    renderCell={renderContactCell}
                    totalCount={totalGroups}
                    rowsPerPage={ROWS_PER_PAGE}
                    page={page}
                    onPageChange={setPage}
                    selectedContacts={selectedContacts}
                    onSelectionChange={handleSelectionChange}
                    sortDescriptor={sortDescriptor}
                    onSortChange={setSortDescriptor}
                    selectedGroupIds={selectedGroupIds}
                    onToggleGroupSelect={toggleGroupSelection}
                    height="100%"
                  />
                </div>
              )
            ) : contacts.length === 0 && totalCount === 0 && !isLoading ? (
              searchValue.length > 0 || appliedFilterCount > 0 ? (
                <NoDataFound
                  title="No Contacts Found"
                  description="No contacts found for the selected search or filters"
                  searchValue={searchValue}
                />
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[200px] gap-2 flex-1">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    No Contacts Found
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Add contacts to your account to add them as leads.
                  </p>
                </div>
              )
            ) : contacts.length > 0 && !isLoading ? (
              <div className="flex-1 min-h-0 w-full flex flex-col">
                <CustomTable
                  data={contacts}
                  columns={ContactColumns?.filter(
                    (col) => col.uid !== "actions",
                  )}
                  renderCell={renderContactCell}
                  totalCount={totalCount}
                  rowsPerPage={ROWS_PER_PAGE}
                  setRowsPerPage={() => {}}
                  page={page}
                  onPageChange={setPage}
                  enableSelection
                  selectedItems={selectedContacts}
                  setSelectedItems={setSelectedContacts}
                  onSelectionChange={handleSelectionChange}
                  height="100%"
                  pinFirstColumn
                  showCellBorders
                  pl="pl-2"
                  pr="pr-2"
                />
              </div>
            ) : null}

            {selectedContacts.length > 0 && (
              <div className="flex justify-end pt-2">
                <PrimaryButton
                  onClick={handleAddSelected}
                  disabled={isBusy}
                  isLoading={isBusy}
                  startContent={false}
                >
                  {isGeneratingMatchScore
                    ? "Generating scores..."
                    : isAdding
                      ? "Adding..."
                      : `Add Selected (${selectedContacts.length})`}
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
      </CustomModal>

      {!showReview && (
        <ContactFiltersUI
          showfilters={showfilters}
          setShowfilters={setShowfilters}
          filters={filters}
          setFilters={setFilters}
          appliedFilters={appliedFilters}
          clearFilters={clearFilters}
          onUpdate={applyFilters}
          zIndex={100005}
        />
      )}

      <MatchScoreReviewModal
        isOpen={isOpen && showReview}
        contacts={reviewContacts}
        campaign={{ id: campaignId, name: campaign?.name || "this campaign" }}
        onClose={onClose}
        onBack={() => setShowReview(false)}
        backButtonLabel="Back to lead selection"
        onConfirm={handleConfirmEnrollment}
        isEnrolling={isAdding}
      />
    </>
  );
};

export default ContactsListModal;
